import { useState, useEffect } from "react";
import { Switch, Table, Modal, Popconfirm, message } from "antd";
import type { TableProps } from "antd";
import { languagesDataType } from "../../interfaces/admin.interface";
import { useFormik } from "formik";
import { LanguageService } from "../../services/languages.service";
import { Helmet } from "react-helmet";
import * as Yup from "yup";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import ShowImage from "../../components/shared/show-image/ShowImage";
import SelectImage from "../../components/shared/select-image/SelectImage";
import { FaPlus } from "react-icons/fa";

const Languages = () => {
  const [langData, setLangData] = useState<languagesDataType[]>([]);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [editingRecord, setEditingRecord] = useState<languagesDataType | null>(
    null
  );
  const [logoFileList, setLogoFileList] = useState<any[]>([]);
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");
  useEffect(() => {
    setLoading(true);
    LanguageService.getAllLanguages()
      .then((response) => {
        if (Array.isArray(response?.data)) {
          const dataWithKeys = response?.data?.map(
            (item: languagesDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setLangData(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    const updatedData = await LanguageService.getAllLanguages();
    setLangData(
      updatedData?.data?.map((item: languagesDataType, index: number) => ({
        ...item,
        key: index + 1,
      }))
    );
  };
  const {
    handleSubmit,
    handleChange,
    values,
    resetForm,
    setFieldValue,
    touched,
    errors,
  } = useFormik({
    initialValues: {
      name: "",
      code: "",
      image: "",
      is_visible: false,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Dilin adı tələb olunur"),
      code: Yup.string()
        .max(2, "Dilin kodu ən çox 2 simvoldan ibarət olmalıdır")
        .required("Dilin kodu tələb olunur"),
      image: Yup.string().required("Dilin şəkli tələb olunur"),
    }),
    onSubmit: async (values) => {
      if (isEditing && editingRecord) {
        if (editingRecord.image === values.image) {
          const isEqual =
            editingRecord.name === values.name &&
            editingRecord.code === values.code &&
            editingRecord.image === values.image &&
            editingRecord.is_visible === values.is_visible;
          if (isEqual) {
            message.info("Dəyişiklik edilmədi.");
          } else {
            const payload = {
              name: values.name,
              code: values.code,
              is_visible: values.is_visible,
            };
            const response = await LanguageService.updateLanguage({
              ...payload,
              id: editingRecord.id,
            });
            if (response.status === 200) {
              message.success("Dil uğurla yeniləndi!");
              await handleAfterSubmit();
            } else {
              message.error(
                response.errorMessage || "Dili yeniləmək alınmadı."
              );
            }
          }
        } else {
          const response = await LanguageService.updateLanguage({
            ...values,
            id: editingRecord.id,
          });
          if (response.status === 200) {
            message.success("Dil uğurla yeniləndi!");
            await handleAfterSubmit();
          } else {
            message.error(response.errorMessage || "Dili yeniləmək alınmadı.");
          }
        }
      } else {
        const response = await LanguageService.addLanguage(values);
        if (response.status === 201) {
          message.success("Dil uğurla əlavə edildi!");
          await handleAfterSubmit();
        } else {
          message.error(response.errorMessage || "Dil əlavə etmək alınmadı.");
        }
      }
      setLoading(false);
    },
  });

  const handleSwitchChange = async (checked: boolean, recordId: number) => {
    LanguageService.updateLanguage({
      id: recordId,
      is_visible: checked,
    });
    if (checked) {
      message.success("Dil artıq saytda görünür.");
    } else {
      message.info("Dil artıq saytda görünmür.");
    }
    setLangData((prevData) =>
      prevData.map((item) =>
        item.id === recordId ? { ...item, is_visible: checked } : item
      )
    );
  };
  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
    setFieldValue("image", "");
    setLogoFileList([]);
  };
  const handleEditClick = (record: languagesDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);
    setFieldValue("name", record.name);
    setFieldValue("code", record.code);
    setFieldValue("is_visible", record.is_visible);
    setFieldValue("image", record.image);
    setLogoFileList(
      record.image
        ? [
            {
              uid: "-1",
              name: `${record.name} image`,
              status: "done",
              url: record.image,
            },
          ]
        : []
    );
  };

  const handleDeleteClick = async (record: languagesDataType) => {
    try {
      await LanguageService.deleteLanguage(record.id.toString());
      setLangData((prevData) =>
        prevData.filter((item) => item.id !== record.id)
      );
      message.success("Dil uğurla silindi!");
    } catch (error) {
      message.error("Dili silmək alınmadı.");
    }
  };
  const columns: TableProps<languagesDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => a.key - b.key,
    },
    {
      title: "Dil",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Dilin kodu",
      dataIndex: "code",
      sorter: (a, b) => a.code.localeCompare(b.code),
    },
    {
      title: "Bayraq",
      dataIndex: "image",
      render: (_, record) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedImage(record.image);
            setShowImageModal(true);
          }}
        >
          {record.image ? (
            <img
              style={{ width: "50px", height: "50px", borderRadius: "50%" }}
              src={record.image}
              alt={`${record.name}`}
            />
          ) : null}
        </div>
      ),
    },
    {
      title: "Saytda görünürlük",
      dataIndex: "is_visible",
      render: (_, record) => (
        <div className="switch_input">
          <Switch
            checked={record.is_visible}
            onChange={(checked) => handleSwitchChange(checked, record.id)}
          />
        </div>
      ),
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu dili siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silinmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <section id="pages">
      <Helmet>
        <title>BSC | Dillər</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Dillər</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>
            <div className="pages_table">
              <Table<languagesDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={langData}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}
      <Modal
        title={isEditing ? "Dili redaktə edin" : "Yeni dil əlavə edin"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <div className="form_input">
            <label className="label" htmlFor="name">
              Dilin adı
            </label>
            <div className="input_wrapper">
              <input
                name="name"
                id="name"
                placeholder="Dilin adı"
                value={values.name}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.name} touched={touched.name} />
          </div>
          <div className="form_input">
            <label className="label" htmlFor="code">
              Dilin kodu
            </label>
            <div className="input_wrapper">
              <input
                name="code"
                id="code"
                placeholder="Dilin kodu"
                value={values.code}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.code} touched={touched.code} />
          </div>
          <div className="form_input">
            <SelectImage
              image_name="Şəkil seçin"
              logoFileList={logoFileList}
              setLogoFileList={setLogoFileList}
              field="image"
              setFieldValue={setFieldValue}
            />
            <ErrorMessage error={errors.image} touched={touched.image} />
          </div>
          <div className="form_input">
            <label className="active_label" htmlFor="">
              Dilin saytda görünməsi
            </label>
            <Switch
              checked={values.is_visible}
              onChange={(checked) => setFieldValue("is_visible", checked)}
            />
          </div>
          <div>
            <MainButton type="submit" full>
              {isEditing ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
      <ShowImage
        showImageModal={showImageModal}
        setShowImagesModal={setShowImageModal}
        image={selectedImage}
      />
    </section>
  );
};

export default Languages;
